import React from 'react';
import { Button, DatePicker, Form, Input, Select, Upload, Switch } from 'antd';
import { openNotification } from '../../../utils';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { apiFetch } from 'services/apiClient';
import { createExternalDocument, updateExternalDocument, getExternalDocument } from '../services/external-documentService';
import { CreateExternalDocumentDto } from '../types/ExternalDocument';

export default function ExternalDocumentForm({ id, onSuccess, referenceId }: { id?: number; onSuccess: () => void; referenceId?: number }) {
  const [form] = Form.useForm<CreateExternalDocumentDto>();
  const [attributeOptions, setAttributeOptions] = React.useState<any[]>([]);
  const [persistedAttributeOptions, setPersistedAttributeOptions] = React.useState<any[]>([]);
  const [controlAreaOptions, setControlAreaOptions] = React.useState<any[]>([]);
  const [controlAreasLoading, setControlAreasLoading] = React.useState<boolean>(false);
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const selectedAttributeIds = Form.useWatch('attributes', form);
  const MAX_UPLOAD_BYTES = 30 * 1024 * 1024; // 30MB

  const parseErrorMessage = (error: any, fallback: string) => {
    const rawMessage = error?.message || error?.error || error?.toString?.();
    if (rawMessage) {
      try {
        const parsed = JSON.parse(rawMessage);
        const parsedMsg = parsed?.message || parsed?.error;
        if (parsedMsg) return String(parsedMsg);
        if (parsed?.status === 413) {
          return 'Dosya boyutu 30MB sınırını aşıyor. Lütfen daha küçük bir dosya seçin.';
        }
      } catch (_ignore) { /* rawMessage is not JSON */ }
      const lower = rawMessage.toString().toLowerCase();
      if (lower.includes('413') || lower.includes('payload too large')) {
        return 'Dosya boyutu 30MB sınırını aşıyor. Lütfen daha küçük bir dosya seçin.';
      }
      if (lower.includes('failed to fetch') || lower.includes('network')) {
        return 'Sunucu şu anda cevap vermiyor. Lütfen daha sonra tekrar deneyin.';
      }
      return rawMessage.toString();
    }
    return fallback;
  };

  React.useEffect(() => {
    if (id) {
      getExternalDocument(id).then(d => {
        const data: any = { ...d };
        const persistedAttributes = Array.isArray(data.attributes) ? data.attributes : [];
        setPersistedAttributeOptions(
          persistedAttributes
            .map((o: any) => {
              const rawId = Number(o?.id ?? o);
              if (!Number.isFinite(rawId)) {
                return null;
              }
              return {
                id: rawId,
                name: o?.name,
                isActive: o?.isActive,
              };
            })
            .filter(Boolean),
        );
        if (data.publicationDate) data.publicationDate = dayjs(data.publicationDate);
        if (data.lastRevisionDate) data.lastRevisionDate = dayjs(data.lastRevisionDate);
        if (data.attributes) data.attributes = data.attributes.map((o: any) => o.id ?? o);
        if (data.controlAreas) data.controlAreas = data.controlAreas.map((o: any) => o.id ?? o);
        if (data.documentFilePath) {
          const url = data.documentFilePath;
          const name = url.split('/').pop() || 'file';
          data.documentFilePath = [{ uid: '-1', name, status: 'done', url }];
        }
        form.setFieldsValue(data);
      });
    } else {
      form.resetFields();
      setPersistedAttributeOptions([]);
      form.setFieldsValue({ isActive: true });
    }
  }, [id, form]);

  React.useEffect(() => {
    let mounted = true;
    const loadOptions = async () => {
      try {
        const [attributes] = await Promise.all([
          apiFetch('/attributes')
        ]);
        if (!mounted) return;
        setAttributeOptions(attributes || []);
      } catch (error) {
        console.error('Kontrol alanları veya parametreler yüklenemedi', error);
      }
    };
    loadOptions();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const fetchControlAreas = async () => {
      try {
        const rawValues = Array.isArray(selectedAttributeIds) ? selectedAttributeIds : [];
        const normalizedIds = rawValues
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value));
        if (normalizedIds.length === 0) {
          if (!cancelled) {
            setControlAreaOptions([]);
            form.setFieldsValue({ controlAreas: [] });
            setControlAreasLoading(false);
          }
          return;
        }
        setControlAreasLoading(true);
        const responses = await Promise.allSettled(
          normalizedIds.map((attrId) => apiFetch(`/control-areas?attributeId=${attrId}`))
        );
        const merged: any[] = [];
        responses.forEach((result) => {
          if (result.status === 'fulfilled') {
            const list = Array.isArray(result.value) ? result.value : result.value?.data;
            if (Array.isArray(list)) {
              merged.push(...list);
            }
          }
        });
        if (!cancelled) {
          const map = new Map<number, any>();
          merged.forEach((item) => {
            const id = Number(item?.id);
            if (!Number.isFinite(id) || map.has(id)) {
              return;
            }
            map.set(id, item);
          });
          const deduped = Array.from(map.values()).sort((a: any, b: any) => {
            const nameA = String(a?.name ?? `#${a?.id ?? ''}`).toLocaleLowerCase('tr');
            const nameB = String(b?.name ?? `#${b?.id ?? ''}`).toLocaleLowerCase('tr');
            return nameA.localeCompare(nameB);
          });
          if (normalizedIds.length > 0 && deduped.length === 0) {
            form.setFieldsValue({ controlAreas: [] });
          } else {
            const allowedIds = new Set(
              deduped
                .map((item: any) => Number(item?.id))
                .filter((value: number) => Number.isFinite(value)),
            );
            const currentControlAreas = form.getFieldValue('controlAreas');
            if (Array.isArray(currentControlAreas)) {
              const nextControlAreas = currentControlAreas.filter((value: any) =>
                allowedIds.has(Number(value)),
              );
              if (nextControlAreas.length !== currentControlAreas.length) {
                form.setFieldsValue({ controlAreas: nextControlAreas });
              }
            }
          }
          setControlAreaOptions(deduped);
        }
      } catch (error) {
        console.error('Kontrol alanları alınamadı', error);
        if (!cancelled) {
          setControlAreaOptions([]);
        }
      } finally {
        if (!cancelled) {
          setControlAreasLoading(false);
        }
      }
    };
    fetchControlAreas();
    return () => {
      cancelled = true;
    };
  }, [selectedAttributeIds, form]);

  const attributeSelectOptions = React.useMemo(() => {
    const selectedIds = new Set(
      (Array.isArray(selectedAttributeIds) ? selectedAttributeIds : [])
        .map((value: any) => Number(value))
        .filter((value: number) => Number.isFinite(value)),
    );
    const optionMap = new Map<number, any>();

    const addOption = (option: any, forceInclude: boolean = false) => {
      const id = Number(option?.id);
      if (!Number.isFinite(id)) {
        return;
      }
      const isActive = option?.isActive === true;
      if (!forceInclude && !isActive && !selectedIds.has(id)) {
        return;
      }
      if (!optionMap.has(id)) {
        optionMap.set(id, { ...option, id });
      } else {
        optionMap.set(id, { ...optionMap.get(id), ...option, id });
      }
    };

    attributeOptions.forEach((option) => addOption(option));
    persistedAttributeOptions.forEach((option) => addOption(option, selectedIds.has(Number(option?.id))));

    return Array.from(optionMap.values()).sort((a: any, b: any) => {
      const nameA = String(a?.name ?? `#${a?.id ?? ''}`).toLocaleLowerCase('tr');
      const nameB = String(b?.name ?? `#${b?.id ?? ''}`).toLocaleLowerCase('tr');
      return nameA.localeCompare(nameB);
    });
  }, [attributeOptions, persistedAttributeOptions, selectedAttributeIds]);

  const onFinish = async (values: CreateExternalDocumentDto) => {
    setSubmitting(true);
    const data: any = { ...values };
    if (data.publicationDate) data.publicationDate = data.publicationDate.format('YYYY-MM-DD');
    if (data.lastRevisionDate) data.lastRevisionDate = data.lastRevisionDate.format('YYYY-MM-DD');
    if (data.attributes) data.attributes = data.attributes.map((id: any) => id);
    if (data.controlAreas) data.controlAreas = data.controlAreas.map((id: any) => id);
    // documentFilePath backend'de String; boş seçimlerde [] yerine null gönder
    if (Array.isArray(data.documentFilePath)) {
      if (!data.documentFilePath.length) {
        data.documentFilePath = null;
      } else {
        const first = data.documentFilePath[0];
        if (first && first.originFileObj) {
          const fd = new FormData();
          fd.append('code', 'documentFilePath');
          fd.append('document', first.originFileObj);
          try {
            setUploading(true);
            const uploaded = await apiFetch('/documents', { method: 'POST', body: fd });
            data.documentFilePath = uploaded.documentUrl || uploaded.filePath || uploaded;
          } catch (uploadErr) {
            const message = parseErrorMessage(uploadErr, 'Dosya yüklenemedi. Lütfen tekrar deneyin.');
            openNotification('error', 'Hata', message);
            setSubmitting(false);
            setUploading(false);
            return;
          } finally {
            setUploading(false);
          }
        } else if (first && first.url) {
          // keep existing url when editing and no new file selected
          data.documentFilePath = first.url;
        } else {
          data.documentFilePath = null;
        }
      }
    } else if (data.documentFilePath === undefined || data.documentFilePath === '') {
      data.documentFilePath = null;
    }
    try {
      if (id) {
        await updateExternalDocument(id, data);
        openNotification('success', 'Başarılı', 'Dış Kaynaklı Doküman Güncellendi');
      } else {
        await createExternalDocument(data);
        openNotification('success', 'Başarılı', 'Dış Kaynaklı Doküman Eklendi');
      }
      onSuccess();
    } catch (e) {
      const message = parseErrorMessage(e, 'İşlem sırasında bir hata oluştu.');
      openNotification('error', 'Hata', message);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      
      <Form form={form} onFinish={onFinish} layout='vertical' initialValues={{ isActive: true }}>
      <Form.Item name='documentType' label='Doküman Türü' rules={[{ required: true }]}>
        <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' style={{width:'100%'}}><Select.Option value='LEGISLATION'>Mevzuat</Select.Option><Select.Option value='STANDARD'>Standart</Select.Option><Select.Option value='GUIDE'>Rehber</Select.Option><Select.Option value='NORM'>Norm</Select.Option></Select>
      </Form.Item>
      <Form.Item name='documentNumber' label='Doküman Numarası' rules={[{ required: true }]}>
        <Input style={{width:'100%'}} />
      </Form.Item>
      <Form.Item name='documentName' label='Doküman Adı' rules={[{ required: true }]}>
        <Input.TextArea style={{width:'100%'}} />
      </Form.Item>
      <Form.Item name='publicationDate' label='Yayın / Kabul / Revizyon Tarihi' rules={[{ required: true, message: 'Yayın / Kabul / Revizyon Tarihi zorunludur' }]}>
        <DatePicker
          format={APP_LOCAL_DATE_FORMAT}
          placeholder='Seçiniz'
          style={{width:'100%'}}
          disabledDate={(d) => !!d && d.isAfter(dayjs().endOf('day'))}
        />
      </Form.Item>
      <Form.Item name='lastRevisionDate' label='Son Gözden Geçirme Tarihi'>
        <DatePicker
          format={APP_LOCAL_DATE_FORMAT}
          placeholder='Seçiniz'
          style={{width:'100%'}}
          disabledDate={(d) => !!d && d.isAfter(dayjs().endOf('day'))}
        />
      </Form.Item>
      <Form.Item name='documentFilePath' label='Dosya' valuePropName='fileList' getValueFromEvent={e => e && e.fileList}>
        <Upload
          beforeUpload={(file) => {
            if (file.size > MAX_UPLOAD_BYTES) {
              openNotification('error', 'Hata', 'Dosya boyutu 30MB sınırını aşıyor. Lütfen daha küçük bir dosya seçin.');
              return Upload.LIST_IGNORE;
            }
            return false;
          }}
          style={{width:'100%'}}
          disabled={uploading || submitting}
        >
          <Button block loading={uploading}>Yükle</Button>
        </Upload>
      </Form.Item>
      <Form.Item name='isActive' valuePropName='checked' label='Aktif/Pasif'>
        <Switch checkedChildren='Aktif' unCheckedChildren='Pasif' />
      </Form.Item>
      <Form.Item name='attributes' label='Parametre'>
        <Select mode='multiple' showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' style={{width:'100%'}}>
          {attributeSelectOptions.map(o => <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}{o?.isActive === false ? ' (Pasif)' : ''}</Select.Option>)}
        </Select>
      </Form.Item>
      <Form.Item name='controlAreas' label='Parametre Tipleri - Özellikleri'>
        <Select mode='multiple' showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' style={{width:'100%'}} loading={controlAreasLoading} disabled={controlAreasLoading && !controlAreaOptions.length}>
          {controlAreaOptions.map(o => <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>)}
        </Select>
      </Form.Item>
      <Button htmlType='submit' type='primary' block loading={submitting} disabled={uploading}>
        Kaydet
      </Button>
    </Form>
    </>
  );
}
